/**
 * Media Transform Pipeline: applies SFrame encryption/decryption to
 * RTCRtpSender/Receiver using RTCRtpScriptTransform (with
 * createEncodedStreams fallback for older browsers).
 *
 * Each sender encrypts outgoing frames; each receiver decrypts incoming
 * frames. The actual crypto is handled by the shared SFrame Worker.
 */

// ============================================================
// Types
// ============================================================

/** Tracks active transforms so they can be torn down per-peer */
interface PeerTransforms {
  senders: Set<RTCRtpSender>
  receivers: Set<RTCRtpReceiver>
}

/** Browser-specific encoded-streams API (legacy fallback) */
interface EncodedStreamsAPI {
  createEncodedStreams(): {
    readable: ReadableStream
    writable: WritableStream
  }
}

// ============================================================
// State
// ============================================================

const peerTransforms = new Map<string, PeerTransforms>()

/** Lazy detect RTCRtpScriptTransform support */
let _supportsScriptTransform: boolean | null = null

function supportsScriptTransform(): boolean {
  if (_supportsScriptTransform === null) {
    _supportsScriptTransform =
      typeof RTCRtpScriptTransform !== 'undefined'
  }
  return _supportsScriptTransform
}

// ============================================================
// Transform Application
// ============================================================

/**
 * Apply encryption transform to an RTCRtpSender.
 * Outgoing media frames will be encrypted before transmission.
 */
export function applyEncryptionTransform(
  sender: RTCRtpSender,
  peerId: string,
  worker: Worker,
): void {
  ensurePeerEntry(peerId)

  if (supportsScriptTransform()) {
    // Modern API: browser pipes frames through the worker automatically
    sender.transform = new RTCRtpScriptTransform(worker, {
      operation: 'encrypt',
      peerId,
    })
  } else {
    // Legacy fallback: createEncodedStreams on the sender
    const senderWithStreams = sender as unknown as EncodedStreamsAPI
    if (typeof senderWithStreams.createEncodedStreams !== 'function') {
      console.warn('[transforms] No SFrame transform API available — frames will be unencrypted')
      return
    }
    const { readable, writable } = senderWithStreams.createEncodedStreams()
    worker.postMessage(
      { type: 'legacy-transform', operation: 'encrypt', readable, writable },
      [readable, writable] as unknown as Transferable[],
    )
  }

  peerTransforms.get(peerId)!.senders.add(sender)
}

/**
 * Apply decryption transform to an RTCRtpReceiver.
 * Incoming media frames will be decrypted after reception.
 */
export function applyDecryptionTransform(
  receiver: RTCRtpReceiver,
  peerId: string,
  worker: Worker,
): void {
  ensurePeerEntry(peerId)

  if (supportsScriptTransform()) {
    receiver.transform = new RTCRtpScriptTransform(worker, {
      operation: 'decrypt',
      peerId,
    })
  } else {
    const receiverWithStreams = receiver as unknown as EncodedStreamsAPI
    if (typeof receiverWithStreams.createEncodedStreams !== 'function') {
      console.warn('[transforms] No SFrame transform API available — frames will be unencrypted')
      return
    }
    const { readable, writable } = receiverWithStreams.createEncodedStreams()
    worker.postMessage(
      { type: 'legacy-transform', operation: 'decrypt', readable, writable },
      [readable, writable] as unknown as Transferable[],
    )
  }

  peerTransforms.get(peerId)!.receivers.add(receiver)
}

/**
 * Remove all transforms associated with a peer.
 * Clears the RTCRtpScriptTransform on each sender/receiver.
 */
export function removeTransforms(peerId: string): void {
  const entry = peerTransforms.get(peerId)
  if (!entry) return

  for (const sender of entry.senders) {
    try {
      sender.transform = null
    } catch {
      // Already torn down
    }
  }

  for (const receiver of entry.receivers) {
    try {
      receiver.transform = null
    } catch {
      // Already torn down
    }
  }

  peerTransforms.delete(peerId)
}

/**
 * Remove all transforms for all peers.
 */
export function removeAllTransforms(): void {
  for (const peerId of [...peerTransforms.keys()]) {
    removeTransforms(peerId)
  }
}

// ============================================================
// Helpers
// ============================================================

function ensurePeerEntry(peerId: string): void {
  if (!peerTransforms.has(peerId)) {
    peerTransforms.set(peerId, {
      senders: new Set(),
      receivers: new Set(),
    })
  }
}

/** Visible for testing */
export function _resetScriptTransformDetection(): void {
  _supportsScriptTransform = null
}
